/* eslint-disable @typescript-eslint/member-ordering */
import { Component } from '@angular/core';
import { AccountHelper } from 'src/lib/accountHelper';
import { LocalStorageService } from 'src/services/local-storage';
import { UserService } from 'src/services/user.service';
import { AlertController, Platform } from '@ionic/angular';
import { AuthResponse } from 'src/models/auth-response.model';
import { Router } from '@angular/router';
import { StatusBar } from '@capacitor/status-bar';
import { AlertMessager } from 'src/lib/alertMessager';
import { NotificationService } from 'src/services/notifications.service';
import { Capacitor } from '@capacitor/core';
import { AuthenticateService } from 'src/services/authenticateService';
import { AppInsightsService } from 'src/services/app-insights.service';
import { App } from '@capacitor/app';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { MembershipService } from 'src/services/membership.service';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { MediaService } from 'src/services/media.service';
import { LiveUpdateService } from 'src/services/liveupdate.service';
import { UserManager } from 'src/services/managers/user.manager';
import { DocumentService } from 'src/services/document.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  refreshInterval: any;
  isLoading: boolean;
  private isAuthenticated = true;
  constructor(
    private platform: Platform,
    private router: Router,
    private membershipService: MembershipService,
    private authService: AuthenticateService,
    private localStorage: LocalStorageService,
    private userService: UserService,
    private notificationService: NotificationService,
    private alert: AlertMessager,
    private alertCtrl: AlertController,
    private insights: AppInsightsService,
    private mediaService: MediaService,
    private liveUpdateService: LiveUpdateService,
    private userManager: UserManager,
    private documentService: DocumentService
  ) {
    this.initializeApp();
    this.refreshUserData();
  }

  refreshUserData() {
    if (!AccountHelper.instance().token) {
      this.refreshInterval = setInterval(() => {
        if (!this.isLoading) {
          this.refreshUserData();
        }
      }, 3600000);
    } else {
      this.isLoading = true;
      this.authService.refreshUserData()
        .then(data => {
          this.localStorage.updateEntity('authtokens', data)
            .then(() => {
              AccountHelper.instance().loadToken(data);
              this.userService.getUser(data.id, true);
            });

          if (!this.refreshInterval) {
            this.refreshInterval = setInterval(() => {
              if (!this.isLoading) {
                this.refreshUserData();
              }
            }, 3600000);
          }
        }
        ).catch(err => {
          if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
          }
          //show logout message;
          this.alert.showError(
            'Your session has expired.',
            'Please log in to continue.');

          this.authService.logout();
        })
        .finally(() => {this.isLoading = false;});
    }
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.localStorage.getEntity<AuthResponse>('authtokens')
        .then(async data => {          
          if (Capacitor.isNativePlatform()) {
            const info = await App.getInfo();
            this.insights.logEvent(this.insights.eventNames.session.start, {version: info.version, build: info.build, id: info.id, platform: Capacitor.getPlatform()});
          } else {
            this.insights.logEvent(this.insights.eventNames.session.start, {platform: Capacitor.getPlatform()});
          }

          if (Capacitor.isNativePlatform()) {
            await this.handleStatusBar();
          }

          if (!data) {
            this.isAuthenticated = false;
            this.router.navigate(['auth/login'], { replaceUrl: true });
          } else {
            AccountHelper.instance().loadToken(data);
            await this.userService.getUser(data.id, true);

            // console.log('app component account', this.userManager.getCurrentAccount());
            // this.documentService.getMissingAccountDocuments(data.id, []).then((x) => {
            //   if (x && x.length > 0) {
            //     this.documentService.showMissingDocumentsModal(this.userManager.getCurrentAccount(), x);
            //   }
            // });

            // console.log('native', Capacitor.isNativePlatform());
            if (Capacitor.isNativePlatform()) {
              this.setUpNotifications();
            }
          }
        }).catch((err) => {
          this.insights.logException(err);
          this.isAuthenticated = false;
          this.router.navigate(['auth/login'], { replaceUrl: true });
        });

        if (Capacitor.isNativePlatform()) {
          this.checkVersion()
            .then(() => {})
            .catch(err => {
              console.warn(err);
              this.insights.logException(err);
            });

          this.membershipService.initInAppPurchase()
            .then(() => {})
            .catch(err => {
              console.warn(err);
              this.insights.logException(err);
            });
          
          ScreenOrientation.lock({
            orientation: 'portrait-primary'
          });

          this.initPrivacyScreen();
        } else {
          console.log('IAP NOT AVAILABLE');
        }

        App.addListener('appUrlOpen', (data: any) => {
          const path = data.url.replace('nextgenrodeo://', '');
          this.handleDeepLink(path);
        });
    }).catch(err => {
      this.insights.logException(err);
      console.log('app error', err);
    });
  }

  private setUpNotifications() {
    // console.log('set up push');
    this.notificationService.registerPushNotifications()
      .then(() => { console.log('Notifications setup.'); })
      .catch((err) => { console.error('Notifications error', err); });
  }

  private async handleStatusBar() {
    await StatusBar.show();
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false });
    }
  }

  async login() {
    await this.localStorage.purge();
    await this.router.navigate(['auth/login'], { replaceUrl: true });
  }

  private async checkVersion() {
    const info = await App.getInfo();
    // console.log('APP INFO', info);
    info.version
    // console.log('App Version: ', info.version)
    if (Capacitor.isNativePlatform()) {
      const result = await AppUpdate.getAppUpdateInfo();
      console.log('App Update Availability', result);
      if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        return;
      }
  
      if (result.immediateUpdateAllowed && Capacitor.getPlatform() === 'android') {
        await AppUpdate.performImmediateUpdate();
      } else {
        const alert = await this.alertCtrl.create({
          message: 'Please update your app to continue.', 
          buttons: ['Update']
        });
        await alert.present();
        await alert.onDidDismiss();
        await AppUpdate.openAppStore();
      }
    }
  }

  private initPrivacyScreen() {
    PrivacyScreen.disable().then(() => console.log('Privacy screen disabled'));

    PrivacyScreen.addListener('screenRecordingStarted', () => {
      this.mediaService.userEnabledScreenRecording.next(true);
    });

    PrivacyScreen.addListener('screenRecordingStopped', () => {
      this.mediaService.userEnabledScreenRecording.next(false);
    });
  }

  private handleDeepLink(path: string) {
    console.log(path);
    this.insights.logEvent(this.insights.eventNames.session.deepLink, {deepLink: path});
    if (path.startsWith('memberships')) {
      const queryString = path.split('?')[1];
      const queryParams = new URLSearchParams(queryString);
      console.log(queryParams);
      if (!this.isAuthenticated) {
        this.userService.navigateToAfterAuthUrl = `tabs/profile/memberships?orgId=${queryParams.get('orgId')}`;
      } else {
        this.router.navigate(['tabs', 'profile', 'memberships'], {queryParams: {orgId: queryParams.get('orgId')}});
      }
    }
  }
}
